<template>
	<div class="step-content">
		<!-- Title & Send Button -->
		<div class="row px-3">
			<h3 class="col-12 hr-title">Upload & Send Settlement Advice</h3>
			<div class="w-100 text-center pb-3">This section will show the following document types: Settlement advice.</div>
			<div class="d-flex ml-auto">
				<application-audit
					:disabled="!checklistEnabled"
					v-if="auditSettings.location == 'settlement' && Ambition.useApplicationAudits()"
					role="broker"
				></application-audit>

				<button
					class="btn-green ml-auto"
					:disabled="sendSettlementAdviceButtonDisabled"
					@click="openSendSettlementAdviceModal"
				>
					Send Settlement Advice
				</button>
			</div>
		</div>

		<!-- File Group -->
		<FileGroup
			v-if="settlementAdviseDocuments.length > 0"
			:doc-types="fileTypes"
			:selected_docs.sync="settlementDocuments"
			:documents="settlementAdviseDocuments"
			:selectable="true"
			@changeFileType="changeDocCategory"
			@changeDocCategory="changeDocCategory"
			@viewDocument="viewDocument"
			@deleteDocument="deleteDocument"
			@getDocuments="getDocuments"
		/>
		<div v-else class="mb-5" />

		<!-- Input Fields -->
		<div class="row">
			<div class="col-12 d-flex flex-end">
				<file-upload
					:modal="true"
					v-if="dropzoneOptions.url"
					:endpoint="dropzoneOptions.url"
					@uploadSuccess="successSending"
					:extraFields="settlementAdvice()"
					uploadTriggerText="Upload Settlement Advice"
					label="Upload Settlement Advice <b><em>or</em></b> <span style='text-decoration: underline'>click to browse</span>"
					:nomargin="true"
				/>
			</div>
		</div>
		<div class="row mt-3">
			<div class="col-3">
				<div class="input-belt mb-3">
					<label>
						Remittance Date
						<span class="text-danger">*</span>
					</label>
					<el-date-picker
						class="w-100 input-belt"
						v-model="state.remittance_date"
						value-format="yyyy-MM-dd"
						format="dd/MM/yyyy"
						type="date"
						placeholder="Select Remittance Date"
					></el-date-picker>
				</div>
			</div>

			<application-input class="col-3 input-belt mb-3" v-model="state.lender_contract_number">
				Lender Contract Number
				<span class="text-danger">*</span>
			</application-input>

			<div class="row mb-3 px-3 table-row">

				<div class="col-12 d-flex align-items-center">
					<application-checkbox
						style="margin: 0 !important; padding: 0 !important"
						@input="loanApproved"
						:checked="application.summary_matches_lender"
					>
						<span class="pl-3">
							<!-- Does the information on this summary tab match exactly to what is in the lender system? -->
							I confirm that the information on this final loan summary tab matches the lender submission.
						</span>
					</application-checkbox>
				</div>
			</div>
		</div>

		<seller-modal
			v-if="headers"
			:headers="headers"
			:remittance-date="state.remittance_date"
			:lender-contract-number="state.lender_contract_number"
			:open-modal.sync="openSettlementModal"
			:settlement-documents="settlementDocuments"
			:doc-documents="documents"
			:applicants="applicants"
		></seller-modal>

		<builder-send-settlement-advice
			v-if="headers"
			:visible.sync="sendSettlementAdviceModalVisible"
			:headers="headers"
			:remittance-date="state.remittance_date"
			:lender-contract-number="state.lender_contract_number"
			:settlement-documents="settlementDocuments"
			:doc-documents="documents"
			:applicants="applicants"
		></builder-send-settlement-advice>
	</div>
</template>

<script>
	import { mapGetters } from "vuex"
	import { GlobalEventBus } from "@tenant/shared/GlobalEventBus"
	import SellerModal from "@ambition/components/send-and-settle-documents/SellerModal"
	import BuilderSendSettlementAdvice from "@shared/components/SendSettlementAdvice/BuilderSendSettlementAdvice"
	import ApplicationAudit from "@ambition/components/ApplicationAudit/ApplicationAudit"

	export default {
		name: "SendSettlementAdvice",
		components: {
			"seller-modal": SellerModal,
			BuilderSendSettlementAdvice,
			ApplicationAudit,
		},
		props: {
			lender_contract_number: { type: [String], default: "" },
			remittance_date: { type: [String], default: "" },
			application: {
				type: Object,
				required: true,
			},
		},
		data() {
			return {
				sendSettlementAdviceModalVisible: false,
				documents: [],
				bundles: [],
				// fileTypes: [],
				settlementDocuments: [],
				dropzoneOptions: {},
				settlementReady: false,
				openSettlementModal: false,
				headers: {},
				applicants: [],
			}
		},
		computed: {
			...mapGetters({
				state: "SendAndSettle/getState",
				docDocuments: "documents/all",
				fileTypes: "documents/fileTypes",
				auditSettings: "applicationAudit/getAuditSettings",
			}),

            sendSettlementAdviceButtonDisabled() {
                if (!this.ableToProceed) return true
                if (this.appCompleted) return true
                if (this.auditSettings.location == 'settlement' && this.auditChecklistRequired) return true
                if (!this.application.summary_matches_lender) return true
                return false
            },

			auditChecklistRequired() {
				if (!Ambition.useApplicationAudits()) return false
				if (this.auditSettings.required == "no") return false
				if (Ambition.auditChecklistRequired()) {
					if (_.size(this.applicationAuditDocuments.filter((doc) => doc.name == "Broker Checklist")) <= 0) {
						return true
					}
				}
				return false
			},

			checklistEnabled() {
				if (this.auditSettings.required == "no") return true
				if (
					this.state.remittance_date &&
					this.state.remittance_date.length > 0 &&
					this.state.lender_contract_number &&
					this.state.lender_contract_number.length > 0
				)
					return true
				return false
			},

			applicationAuditDocuments() {
				return this.docDocuments.filter(
					(doc) => doc.document_type && doc.document_type.sections.some((section) => section.name == "Application Audit")
				)
			},

			settlementAdviseDocuments() {
				return this.documents.filter(
					(doc) => doc.document_type && doc.document_type.sections.some((section) => section.name == "Settlement Advice")
				)
			},

			ableToProceed() {
				if (
					this.state.remittance_date &&
					this.state.remittance_date.length > 0 &&
					this.state.lender_contract_number &&
					this.state.lender_contract_number.length > 0 &&
					this.settlementDocuments &&
					this.settlementDocuments.length > 0
				)
					return true
				return false
			},

			appCompleted() {
				return this.$store.getters.initAppData.application_status_id == 15 && !this.$store.getters.canEditCompleted
			},
		},
		watch: {
			lender_contract_number(newVal, oldVal) {
				this.state.lender_contract_number = newVal
			},
			remittance_date(newVal, oldVal) {
				this.state.remittance_date = newVal
			},
		},
		mounted() {
			// this.state.remittance_date = this.$store.getters.initAppData.remittance_date
			// this.settlementReady = this.$store.getters.initAppData.application_status_id >= 13

			// this.initFileTypes()
			this.selectedDocs = []
			this.applicants = this.$store.getters["application/applicants"]

			this.headers = {
				headers: { Authorization: "Bearer " + this.$store.getters.token },
			}

			this.dropzoneOptions.url = "/api/application/conditioning/" + this.$store.getters.appId + "/document"
			this.dropzoneOptions.headers = this.headers.headers

			this.getDocuments()
			// this.getLenderData()

			// this.$store.dispatch('SendAndSettle/loadApiData')

			this.$store.dispatch("applicationAudit/setAuditSettings")
		},
		updated() {
			this.handleUpdate()
		},
		activated() {
			// GlobalEventBus.$on('saveButton:saved', () => {
			//   alert('something was saved...')
			// })
			// this.$store.dispatch('SendAndSettle/saveSettlementAdvice')
		},
		deactivated() {
			// GlobalEventBus.$off('saveButton:saved')
			// this.$store.dispatch('SendAndSettle/saveSettlementAdvice')
		},
		methods: {
			// Save summary_matches_lender so we know if broker is allowed to proceed to Send & Settle
			loanApproved(checked) {
				// Toggle the checkbox on or off
				this.application.summary_matches_lender = checked

				if (checked) this.$parent.submitApproval()

				const summary_matches_lender = checked

				axios
					.put(
						`/api/v3/application/${this.$store.getters.appId}/confirm-fls`,
						{
							summary_matches_lender,
						},
						{
                            headers: { Authorization: "Bearer " + this.$store.getters.token }
                        }
					)
					.then(async ({ data }) => {
						//
					})
			},
			openSendSettlementAdviceModal() {
				if (Ambition.useEmailBuilder()) {
					this.$store.dispatch("builderEmail/fetchEmailRecipients")
					this.sendSettlementAdviceModalVisible = true
				} else {
					this.openSettlementModal = true
				}
			},
			handleUpdate() {
				const data = {
					lender_contract_number: this.state.lender_contract_number,
					remittance_date: this.state.remittance_date,
				}
				// return console.warn('handleUpdate()', data)
				this.$emit("update", data)
			},

			// getLenderData() {
			//   axios
			//     .get(
			//       `/api/applications/${this.$store.getters.appId}/send-and-settle/lender`,
			//       this.headers
			//     )
			//     .then(res => {
			//       this.application = res.data

			//       // this.state.lender_contract_number = res.data.loan_tiers && res.data.loan_tiers.pivot
			//       //     ? res.data.loan_tiers.pivot.lender_contract_number
			//       //     : null
			//     })
			//     .catch(err => {
			//       console.error(err)
			//     })
			// },

			initFileTypes() {
				let adminUrl = process.env.MIX_APP_URL
				let adminHeaders = {
					headers: { Authorization: "Bearer " + this.$store.getters.adminToken },
				}
				axios.get(adminUrl + "/api/documents", adminHeaders).then((res) => {
					this.fileTypes = res.data
					this.fileTypes.push({
						id: null,
						name: "Others",
					})
				})
			},

			getDocuments() {
				let headers = {
					headers: { Authorization: "Bearer " + this.$store.getters.token },
				}

				axios
					.get("/api/application/" + this.$store.getters.initAppData.id + "/finalise/documents", {
						headers: this.headers.headers,
						params: {
							appends: "signatureStages",
						},
					})
					.then((res) => {
						this.documents = res.data.documents // migrate this
						this.bundles = res.data.bundles
					})
			},

			changeDocCategory(payload) {
				let doc    = _.get(payload, 'doc', payload);
				let status = _.get(payload, 'status', false);

				if (this.appCompleted) {
					return false
				}
				axios
					.put(
						"/api/application/" + this.$store.getters.initAppData.id + "/finalise/documents/" + doc.id,
						{
							document_type_id: doc.document_type_id,
							is_app_document: doc.is_app_document,
							document_category: doc.doc_category,
							status: doc.status,
							file_name: doc.filename,
						},
						this.headers
					)
					.then((res) => {
						const itemIndex = this.documents.findIndex((doc) => {
							return doc.id == res.data.id
						})
						if (!status) {
							this.documents.splice(itemIndex, 1)
							this.documents.push(res.data)
						}
					})
			},

			viewDocument(doc) {
				axios
					.get(
						"/api/application/" +
							this.$store.getters.initAppData.id +
							"/finalise/documents/" +
							doc.id +
							"?is_app_document=" +
							doc.is_app_document,
						this.headers
					)
					.then((res) => window.open(res.data))
			},

			async deleteDocument(doc) {
				axios
					.delete("/api/application/" + this.$store.getters.initAppData.id + "/finalise/documents/" + doc.id, {
						...this.headers,
						data: { is_app_document: doc.is_app_document },
					})
					.then((res) => {})
			},

			getDocuments() {
				let headers = {
					headers: { Authorization: "Bearer " + this.$store.getters.token },
				}

				axios
					.get("/api/application/" + this.$store.getters.initAppData.id + "/finalise/documents", {
						headers: this.headers.headers,
						params: {
							appends: "signatureStages",
						},
					})
					.then((res) => {
						this.documents = res.data.documents // migrate this
						this.bundles = res.data.bundles
					})
			},

			sendSettlementAdvice() {
				if (this.state.remittance_date === "") {
					Message({
						message: "Remittance Date Should be Filled",
						type: "warning",
					})
				} else if (!this.state.lender_contract_number) {
					Message({
						message: "Empty Lender Contract Number",
						type: "warning",
					})
				} else {
					this.openSettlementModal = true
				}
			},

			settlementAdvice() {
				if (!this.fileTypes.length) return
				let docType = this.fileTypes.find((fileType) => {
					return fileType.send_settle_contract == 4
				})
				return {
					document_type_id: docType.id,
					document_category: "internal",
					document_type_name: docType.name,
				}
			},

			successSending() {
				this.$message({
					message: "File uploaded.",
					type: "success",
				})

				this.getDocuments()
			},
		},
	}
</script>

<style lang="scss" scoped>
	// @import '@assets/sass/backend/_core-variables.scss'
</style>
